class VariantSelects extends HTMLElement {
  constructor () {
    super()
    this.addEventListener('change', this.onVariantChange.bind(this))
  }

  onVariantChange () {
    this.updateOptions()
    this.updateCurrentVariant()
    this.updateUI()
  }

  updateOptions () {
    this.options = Array.from(this.querySelectorAll('select'), select => select.value)
  }

  updateCurrentVariant () {
    const variants = this.getVariantData()
    this.currentVariant = variants.find(variant =>
      variant.options.every((option, index) => this.options[index] === option),
    )
  }

  updateUI () {
    this.removeErrorMessage()

    if (!this.currentVariant) {
      this.setUnavailable()
    } else {
      this.updateURL()
      this.updateVariantInput()
      this.renderProductInfo()
      this.dispatchVariantChangeEvent()
      this.updateShareUrl()
      this.updateAddToCartButtonText()
      this.updateOOSAndBuyButton()
    }
  }

  updateAddToCartButtonText () {
    // Try to find the add button in the new design first
    const addButton = document.querySelector('[data-product-submit]') || document.querySelector(`[name="add"]`)
    const addButtonText = addButton?.querySelector('span')

    if (addButton && addButtonText) {
      if (!this.currentVariant.available) {
        addButton.disabled = true
        addButtonText.textContent = window.variantStrings.soldOut || 'Sold out'
      } else {
        addButton.disabled = false
        addButtonText.textContent = window.variantStrings.addToCart || 'Add to cart'
      }
    }
  }

  dispatchVariantChangeEvent () {
    const productImageCarouselEl = document.querySelector('product-media')
    if (!productImageCarouselEl) return
    const variantChangeEvent = new CustomEvent('variantChange', {
      detail: { variant: this.currentVariant },
      bubbles: true,
    })
    productImageCarouselEl.dispatchEvent(variantChangeEvent)
  }

  updateURL () {
    if (this.currentVariant && this.dataset.updateUrl !== 'false') {
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`)
    }
  }

  updateVariantInput () {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
    productForms.forEach(productForm => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = this.currentVariant.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  removeErrorMessage () {
    const section = this.closest('section')
    const productForm = section?.querySelector('product-form')
    productForm?.handleErrorMessage()
  }

  renderProductInfo () {
    this.productInfoElement?.setAttribute('loading', 'true')
    const productPriceElement = document.querySelector('.product-price')
    if (productPriceElement) {
      productPriceElement.setAttribute('loading', 'true')
      productPriceElement.setAttribute('aria-busy', 'true')
    }

    // Construct URL properly - this.dataset.url already includes the locale prefix
    let productUrl = this.dataset.url
    // Remove leading slash if present
    if (productUrl.startsWith('/')) {
      productUrl = productUrl.slice(1)
    }

    const fetchUrl = `/${productUrl}?variant=${this.currentVariant.id}&section_id=${this.dataset.originalSection || this.dataset.section}`

    fetch(fetchUrl)
      .then(response => response.text())
      .then(responseText => this.updateProductInfo(responseText))
      .finally(() => {
        setTimeout(() => {
          this.productInfoElement?.setAttribute('loading', 'false')
          const productPriceEl = document.querySelector('.product-price')
          if (productPriceEl) {
            productPriceEl.removeAttribute('loading')
            productPriceEl.removeAttribute('aria-busy')
          }
        }, (typeof this.loadingDelay === 'number') ? this.loadingDelay : 100)
      })

    // Also update sticky add to cart section
    this.updateStickyAddToCart()
  }

  updateStickyAddToCart () {
    const stickySection = document.querySelector('product-sticky-add-to-cart')
    if (!stickySection) return

    const sectionId = stickySection.getAttribute('data-section-id')
    if (!sectionId) return

    // Construct URL for sticky section
    let productUrl = this.dataset.url
    if (productUrl.startsWith('/')) {
      productUrl = productUrl.slice(1)
    }

    const fetchUrl = `/${productUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`

    fetch(fetchUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const newStickySection = html.querySelector('product-sticky-add-to-cart')
        if (newStickySection && stickySection) {
          // Preserve the current visibility state
          const currentClasses = stickySection.className
          const isVisible = stickySection.classList.contains('product-sticky-add-to-cart--show')

          // Update the content
          stickySection.innerHTML = newStickySection.innerHTML

          // Restore the visibility state
          stickySection.className = currentClasses
          if (isVisible) {
            stickySection.classList.add('product-sticky-add-to-cart--show')
          }
        }
      })
      .catch(error => {
        console.warn('Failed to update sticky add to cart:', error)
      })
  }

  updateProductInfo (responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html')

    // For new design, we need to update price elements directly
    // since they don't have specific IDs like the old design
    this.updateNewDesignPrice(html)
    this.updateNewDesignStock(html)

    // For backward compatibility, try to update old structure elements if they exist
    this.updateElementInnerHtml(`price-${this.dataset.section}`, html)
    this.updateElementInnerHtml(`availability-info-${this.dataset.section}`, html)
    this.updateElementInnerHtml(`stock-notification-${this.dataset.section}`, html)
    this.updateElementInnerHtml(`sale-tag-${this.dataset.section}`, html)

    const price = document.getElementById(`price-${this.dataset.section}`)
    price?.classList.remove('visibility-hidden')

    this.updateAddToCartButtonText()
  }

  updateNewDesignPrice (html) {
    // Find the product-price element in the current page
    const currentPriceElement = document.querySelector('.product-price')
    if (!currentPriceElement) return

    // Find the product-price element in the fetched HTML
    const newPriceElement = html.querySelector('.product-price')
    if (!newPriceElement) return

    // Update the price content
    currentPriceElement.innerHTML = newPriceElement.innerHTML

    // Update finance module if it exists (handle both finance-module and finance-module-old)
    const currentFinanceModule = currentPriceElement.querySelector('finance-module, finance-module-old')
    const newFinanceModule = newPriceElement.querySelector('finance-module, finance-module-old')
    if (currentFinanceModule && newFinanceModule) {
      currentFinanceModule.innerHTML = newFinanceModule.innerHTML
      const newDataAttributes = newFinanceModule.dataset
      Object.keys(newDataAttributes).forEach(key => {
        currentFinanceModule.dataset[key] = newDataAttributes[key]
      })

      if (currentFinanceModule.updateFinanceFromText) {
        const financeLinkText = currentFinanceModule.querySelector('.price__finance-link-text')
        if (financeLinkText) {
          if (typeof currentFinanceModule.loading !== 'undefined') {
            currentFinanceModule.loading = true
          }
          currentFinanceModule.updateFinanceFromText(financeLinkText)
        }
      }
    }
  }

  updateNewDesignStock (html) {
    // Find the product-stock element in the current page
    const currentStockElement = document.querySelector('product-stock')
    if (!currentStockElement) return

    // Find the product-stock element in the fetched HTML
    const newStockElement = html.querySelector('product-stock')
    if (!newStockElement) return

    // Update the stock content
    currentStockElement.innerHTML = newStockElement.innerHTML

    // Copy over data attributes that might be needed
    const newDataAttributes = newStockElement.dataset
    Object.keys(newDataAttributes).forEach(key => {
      currentStockElement.dataset[key] = newDataAttributes[key]
    })

    // Copy over any classes that might have changed
    currentStockElement.className = newStockElement.className
  }

  updateElementInnerHtml (elementId, html) {
    const destination = document.getElementById(elementId)
    const source = html.getElementById(elementId)
    if (source && destination) {
      destination.innerHTML = source.innerHTML
    }
  }

  setUnavailable () {
    const addButton = document.querySelector(`[name="add"]`)
    const addButtonText = addButton?.querySelector('span')

    if (addButton && addButtonText) {
      addButton.disabled = true
      addButtonText.textContent = window.variantStrings.unavailable

      // For new design, update the price to show unavailable state
      const priceElement = document.querySelector('.product-price .price__with-currency')
      if (priceElement) {
        priceElement.textContent = window.variantStrings.unavailable || 'Unavailable'
      }

      // For backward compatibility, try to hide price element if it exists
      const price = document.getElementById(`price-${this.dataset.section}`)
      price?.classList.add('visibility-hidden')
    }
  }

  updateShareUrl () {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`)
    if (shareButton?.updateUrl) {
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`)
    }
  }

  getVariantData () {
    if (!this.variantData) {
      this.variantData = JSON.parse(this.querySelector('[type="application/json"]').textContent)
    }
    return this.variantData
  }

  get productInfoElement () {
    // Try to find the new product-body element first, fallback to old structure
    return document.querySelector('.product-body') || document.getElementById(`ProductInfo-${this.dataset.section}`)
  }

  get loadingDelay () {
    return Number(this.dataset.loadingDelay) || 100
  }

  updateOOSAndBuyButton () {
    const oosBlock = document.querySelector('[data-oos-block]')
    const addButton = document.querySelector('[data-product-submit]')
    if (!oosBlock || !addButton) return
    if (!this.currentVariant.available) {
      oosBlock.classList.remove('hidden')
      addButton.disabled = true
      addButton.classList.add('disabled:opacity-50', 'disabled:pointer-events-none')
      addButton.querySelector('span').textContent = window.variantStrings.soldOut || 'Sold out'
    } else {
      oosBlock.classList.add('hidden')
      addButton.disabled = false
      addButton.classList.remove('disabled:opacity-50', 'disabled:pointer-events-none')
      addButton.querySelector('span').textContent = window.variantStrings.addToCart || 'Add to cart'
    }
  }
}

customElements.define('variant-selects', VariantSelects)

class VariantRadios extends VariantSelects {
  updateOptions () {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    this.options = fieldsets.map((fieldset, optionIndex) => {
      const checkedRadio = Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked,
      )

      // Just use the display value since Shopify stores translated values in variant data
      return checkedRadio.value
    })
  }
}

customElements.define('variant-radios', VariantRadios)
